import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import ownInfoAPI from '../../api/ownInfoAPI';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type { OwnInfoState, UpdateNotificationParams } from '../../types/ownInfo';
import type { OrganizationUserType, UserWithOrganizationType } from '../../types/user';

const initialState: OwnInfoState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  ownInfo: undefined,
};

const slice = createSlice({
  name: 'OwnInfo',
  initialState,
  reducers: {
    startLoading(state: OwnInfoState) {
      state.isLoading = true;
    },

    stopLoading(state: OwnInfoState) {
      state.isLoading = false;
    },

    hasError(state: OwnInfoState, action: PayloadAction<string>) {
      // console.log(action.payload);
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = action.payload;
    },

    resetMessage(state: OwnInfoState) {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
    },

    getOwnInfo(state: OwnInfoState, action: PayloadAction<UserWithOrganizationType>) {
      state.isLoading = false;
      state.ownInfo = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Get OwnInfo
      // ----------------------------------------
      .addCase(getOwnInfo.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOwnInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ownInfo = action.payload;
      })
      .addCase(getOwnInfo.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Update Notification
      // ----------------------------------------
      .addCase(updateNotification.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        const { organizationId, emailNotificationFlag, emailNotificationCustomers } = action.payload;
        if (state.ownInfo) {
          const newOrganizationUser: OrganizationUserType[] = state.ownInfo.organizations.map((orgUser) => {
            if (orgUser.organizationId === organizationId) {
              const newOrgUser: OrganizationUserType = Object.assign({}, orgUser, { emailNotificationFlag, emailNotificationCustomers });
              return newOrgUser;
            } else {
              return orgUser;
            }
          });
          state.ownInfo.organizations = newOrganizationUser;
        }
      })
      .addCase(updateNotification.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateNotificationParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      });
  },
});

export const { reducer, actions } = slice;

export const startLoading =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.startLoading());
  };
export const stopLoading =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.stopLoading());
  };

export const hasError =
  (message: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const getOwnInfo = createAsyncThunk('OwnInfo/getOwnInfo', async (_, thunkAPI) => {
  try {
    const response = await ownInfoAPI.getOwnInfo();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateNotification = createAsyncThunk('OwnInfo/updateNotification', async (params: UpdateNotificationParams, thunkAPI) => {
  try {
    const response = await ownInfoAPI.updateNotification(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
